import React, {Component} from 'react';
import {connect} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import get from 'lodash/get';
import classNames from 'classnames';
import {constants} from '@neosvoc/nv-platform-core-common';
import {
    requestSurveyList, requestDeleteSurvey, changeSurveyListPage,
    requestDeleteSurveyInstances, requestExportSurvey, changeSurveyListFilter,
} from '../../actions/action_survey_list';
import Loader from '../Loader';
import Pager from '../Pager';
import Title from '../Title';
import SurveyTable from './SurveyTable';
import SurveyEmptyList from './SurveyEmptyList';
import Alert from '../Alert';
import Icon from '../Icon';
import messages from '../../../res/translations/messages';
import {userHasPermission} from '../../utils/auth';
import PaginationFilter from '../PaginationFilter';

const PAGE_SIZE = 10;

class SurveyList extends Component {
    constructor(props) {
        super(props);
        this.goToPage = this.goToPage.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleDeleteInstances = this.handleDeleteInstances.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handleApplyFilters = this.handleApplyFilters.bind(this);
        this.handleClearFilters = this.handleClearFilters.bind(this);
    }

    componentDidMount() {
        this.loadCurrentPage();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.page !== this.props.page || (prevProps.invalid !== this.props.invalid && this.props.invalid)) {
            this.loadCurrentPage();
        }

        if (this.props.page > 1 && this.props.items.length === 0) {
            this.props.changeSurveyListPage(1);
        }
    }

    loadCurrentPage() {
        const {filterText, sortBy} = this.props;
        this.props.requestSurveyList((this.props.page - 1) * PAGE_SIZE, PAGE_SIZE, filterText, sortBy);
    }

    goToPage(page) {
        this.props.changeSurveyListPage(page);
    }

    handleDelete(id) {
        this.props.requestDeleteSurvey(id);
    }

    handleExport(id, params) {
        this.props.requestExportSurvey(id, params);
    }

    handleDeleteInstances(id) {
        this.props.requestDeleteSurveyInstances(id);
    }

    handleApplyFilters(filterText, sortBy) {
        this.props.changeSurveyListFilter(filterText, sortBy);
        this.props.changeSurveyListPage(1);
        this.props.requestSurveyList((this.props.page - 1) * PAGE_SIZE, PAGE_SIZE, filterText, sortBy);
    }

    handleClearFilters() {
        this.props.changeSurveyListFilter('', '');
        this.props.changeSurveyListPage(1);
        this.props.requestSurveyList((this.props.page - 1) * PAGE_SIZE, PAGE_SIZE, '', '');
    }

    renderActions() {
        const {count, roles} = this.props;
        const canEditSurvey = userHasPermission(roles, constants.PERMISSIONS.SURVEY_EDIT);

        if (count === 0) {
            return null;
        } else {
            return (
                <div>
                    <Link to={canEditSurvey ? '/surveys/create' : ''} className={classNames({disabled: !canEditSurvey}, 'btn btn-primary')}>
                        <Icon icon="new-doc" /> <FormattedMessage {...messages.surveys.button.new} />
                    </Link>
                </div>
            );
        }
    }

    renderContent() {
        const {count, error, items, roles} = this.props;

        if (error) {
            return null;
        }

        const canEditSurvey = userHasPermission(roles, constants.PERMISSIONS.SURVEY_EDIT);

        if (count === 0) {
            return (
                <SurveyEmptyList className="justify-content-center" onCreate={this.handleCreate}>
                    <Link to={canEditSurvey ? '/surveys/create' : ''} className={classNames({disabled: !canEditSurvey}, 'btn btn-primary')}>
                        <Icon icon="new-doc" /> <FormattedMessage {...messages.surveys.button.new} />
                    </Link>
                </SurveyEmptyList>
            );
        } else {
            return <SurveyTable items={items}
                onDelete={this.handleDelete}
                onDeleteInstances={this.handleDeleteInstances}
                onExport={this.handleExport}
            />;
        }
    }

    render() {
        const {count, loading, error} = this.props;

        return (
            <div className="container">
                <Title actions={this.renderActions()}><FormattedMessage {...messages.surveys.title.list} /></Title>
                <div className="row mb-3 align-items-center">
                    <div className="col-auto">
                        <PaginationFilter
                            filterPlaceholder={messages.surveys.list.filterPlaceholder}
                            sortPlaceholder={messages.surveys.list.sort.placeholder}
                            sortOptions={[
                                {value: 'title:asc', label: messages.surveys.list.sort.title.asc},
                                {value: 'title:desc', label: messages.surveys.list.sort.title.desc},
                                {value: 'created_at:asc', label: messages.surveys.list.sort.created_at.asc},
                                {value: 'created_at:desc', label: messages.surveys.list.sort.created_at.desc},
                                {value: 'updated_at:asc', label: messages.surveys.list.sort.updated_at.asc},
                                {value: 'updated_at:desc', label: messages.surveys.list.sort.updated_at.desc},
                            ]}
                            onApply={this.handleApplyFilters}
                            onClear={this.handleClearFilters}
                            initialFilterText={this.props.filterText}
                            initialSortBy={this.props.sortBy}
                            applyLabel={messages.dashboard.buttons.apply}
                            resetLabel={messages.surveys.list.reset}
                        />
                    </div>
                    <div className="col ml-auto text-right">
                        <Pager className="justify-content-end" pages={Math.ceil(count / PAGE_SIZE)} current={this.props.page} onClick={this.goToPage} />
                    </div>
                </div>
                {loading ? <Loader /> : (
                    <>
                        <Alert error={error} />
                        {this.renderContent()}
                    </>
                )}
                <Pager className="justify-content-end" pages={Math.ceil(count / PAGE_SIZE)} current={this.props.page} onClick={this.goToPage} />
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        count: state.surveyList.count,
        page: state.surveyList.page,
        items: state.surveyList.items,
        loading: state.surveyList.loading,
        error: state.surveyList.error,
        invalid: state.surveyList.invalid,
        filterText: state.surveyList.filterText,
        sortBy: state.surveyList.sortBy,
        roles: get(state, 'auth.user.roles', []),
    };
};

export default connect(mapStateToProps,
    {requestDeleteSurvey, requestSurveyList, changeSurveyListPage, changeSurveyListFilter, requestDeleteSurveyInstances, requestExportSurvey},
)(SurveyList);
